import map from "../../app/map";
import utils from "../../app/utils";
import http from "../../app/http";

/**
 * Clase que extiende `L.GridLayer` para manejar capas de puntos de interés (POIs) en un mapa con teselas.
 */
const TiledPoiLayer = L.GridLayer.extend({
    tiledPoi: null, // Propiedad que almacena la configuración de POIs.
    hasHooks: false, // Indica si los hooks están habilitados.
    dataTiles: {}, // Almacena los datos de las teselas.
    syncCounter: 0, // Contador para sincronización de teselas.

    /**
     * Inicializa la capa con las opciones proporcionadas.
     *
     * @param {Object} options - Opciones específicas de la capa.
     * @param {Object} tileOptions - Opciones de las teselas.
     */
    initialize(options, tileOptions) {
        L.setOptions(this, tileOptions);
        this.tiledPoi = options;
        this.ident = options.ident;
    },

    /**
     * Crea una tesela HTML y carga los datos correspondientes.
     *
     * @param {Object} coords - Coordenadas de la tesela.
     * @param {Function} done - Callback que se ejecuta al completar la creación de la tesela.
     * @returns {HTMLElement} La tesela creada.
     */
    createTile(coords, done) {
        const tile = document.createElement("div");
        coords = this._wrapCoords(coords);
        const url = L.Util.template(this.tiledPoi.tileUrl, coords);
        http.get(url)
            .then(this.onTileLoaded.bind(this, this.syncCounter, coords, tile))
            .then(() => done(undefined, tile))
            .catch((error) => {
                utils.logError("TiledPoiLayer", "Error creating poi-tile", error);
                done(error);
            });
        return tile;
    },

    /**
     * Inicializa el contenedor de la capa.
     */
    _initContainer: function() {
        this.displayed = true;
        this._markers = {};
        L.GridLayer.prototype._initContainer.call(this);
    },

    /**
     * Método llamado al agregar la capa al mapa.
     * Registra un evento para ocultar los POIs al iniciar el zoom.
     *
     * @returns {Object} La instancia de la capa.
     */
    onAdd() {
        L.GridLayer.prototype.onAdd.call(this);
        if (this._map)
            this._map.on("zoomstart", this.hidePois, this);
        return this;
    },

    /**
     * Oculta los POIs eliminando todas las teselas y reseteando los cachés.
     */
    hidePois() {
        this._removeAllTiles();
        this.resetCachesDivs();
    },

    /**
     * Controla la visibilidad del contenedor de la capa.
     *
     * @param {boolean} display - Indica si el contenedor debe mostrarse o no.
     */
    containerDisplay(display) {
        if (this._container) {
            this._container.style.display = display ? "block" : "none";
        }
    },

    /**
     * Busca un POI en las teselas basado en coordenadas y dimensiones.
     *
     * @param {number} x - Coordenada X.
     * @param {number} y - Coordenada Y.
     * @param {number} iconWidth - Ancho del ícono del POI.
     * @param {number} iconHeight - Alto del ícono del POI.
     * @param {number} offsetX - Desplazamiento en X.
     * @param {number} offsetY - Desplazamiento en Y.
     * @returns {Object|undefined} El POI encontrado o `undefined` si no se encuentra.
     */
    searchTilesForXY(x, y, iconWidth, iconHeight, offsetX, offsetY) {
        const tileKeys = Object.keys(this.dataTiles);
        const { x: mapX, y: mapY } = this._map._getMapPanePos();
        const relativeX = x - mapX;
        const relativeY = y - mapY;
        const { tileSize: tileSize } = this.options;

        for (let i = 0; i < tileKeys.length; i++) {
            const tileKey = tileKeys[i];
            const tile = this._tiles[tileKey];

            if (tile) {
                const { x: tilePosX, y: tilePosY } = this._getTilePos(tile.coords);

                if (relativeX > tilePosX && relativeX < tilePosX + tileSize && relativeY > tilePosY && relativeY < tilePosY + tileSize) {
                    const tileData = this.dataTiles[tileKey];
                    const tileOffsetX = relativeX - tilePosX;
                    const tileOffsetY = relativeY - tilePosY;

                    for (let j = 0; j < tileData.id.length; j++) {
                        const poiX = tileData.x[j];
                        const poiY = tileData.y[j];
                        const poiId = tileData.id[j];
                        const adjustedX = poiX - offsetX;
                        const adjustedY = poiY - offsetY;

                        if (tileOffsetX > adjustedX && tileOffsetY > adjustedY && tileOffsetX < adjustedX + iconWidth && tileOffsetY < adjustedY + iconHeight) {
                            return {
                                tileEl: tile.el,
                                id: poiId
                            };
                        }
                    }
                    return;
                }
            }
        }
    },

    /**
     * Método llamado al eliminar la capa del mapa.
     *
     * @param {Object} map - El mapa del que se elimina la capa.
     * @returns {Object} La instancia de la capa.
     */
    onRemove(map) {
        this._removeAllTiles();
        this.resetCachesDivs();
        this.removeFromParent(this._container);
        map._removeZoomLimit(this);
        this._container = null;
        this._tileZoom = undefined;
        if (map)
            map.off("zoomstart", this.hidePois, this);
        return this;
    },

    /**
     * Resetea los cachés y elimina las referencias a las teselas.
     */
    resetCachesDivs() {
        this.syncCounter++;
        this._tiles = {};
        this.dataTiles = {};
        this._tilesToLoad = 0;
    },

    /**
     * Resetea la capa eliminando todas las teselas y redibujándolas.
     */
    _reset() {
        this._removeAllTiles();
        this.resetCachesDivs();
        this.redraw();
    },

    /**
     * Maneja la carga de una tesela.
     *
     * @param {number} syncCounter - Contador de sincronización.
     * @param {Object} coords - Coordenadas de la tesela.
     * @param {HTMLElement} tile - Elemento HTML de la tesela.
     * @param {Object} response - Respuesta de la solicitud HTTP.
     */
    onTileLoaded(syncCounter, coords, tile, response) {
        if (response.status === 200 && syncCounter === this.syncCounter && response.data && response.data.id && response.data.id.length) {
            this.renderTile(tile, coords, response.data);
            this.dataTiles[this._tileCoordsToKey(coords)] = response.data;
        }
    },

    /**
     * Elimina un elemento del DOM.
     *
     * @param {HTMLElement} element - El elemento a eliminar.
     */
    removeFromParent(element) {
        const parent = element && element.parentNode;
        if (parent) {
            parent.removeChild(element);
        }
    },

    /**
     * Renderiza una tesela con los datos proporcionados.
     *
     * @param {HTMLElement} tile - Elemento HTML de la tesela.
     * @param {Object} coords - Coordenadas de la tesela.
     * @param {Object} data - Datos de la tesela.
     */
    renderTile(tile, coords, data) {
        if (!this.tiledPoi)
            throw new Error(`Cannot render tile for ${this.ident}, tiledPoi property is not defined!`);
        const scale = 1 / (1 << coords.z);
        const unitScale = scale / 256;
        const offsetX = coords.x * scale;
        const offsetY = coords.y * scale;
        tile.style.width = tile.style.height = "auto";
        const zoom = map.getZoom();
        const zoomScale = Math.pow(2, zoom - Math.round(zoom));
        for (let i = 0; i < data.id.length; i++) {
            if (!this.tiledPoi.displayItem || this.tiledPoi.displayItem(data, i)) {
                const poiX = data.x[i];
                const poiY = data.y[i];
                const poiElement = document.createElement("div");
                poiElement.classList.add("poi-point");
                poiElement.dataset.id = data.id[i];
                poiElement.dataset.lat = String(utils.unitYToLatDeg(offsetY + unitScale * poiY));
                poiElement.dataset.lon = String(utils.unitXToLonDeg(offsetX + unitScale * poiX));
                poiElement.dataset.poi = this.ident;
                poiElement.style.transform = `translate(${poiX}px, ${poiY}px) scale(${1 / zoomScale})`;
                this.tiledPoi.updateElement(poiElement, data, i);
                tile.appendChild(poiElement);
            }
        }
        tile.classList.add("leaflet-tile-loaded");
    },

    /**
     * Elimina una tesela específica.
     *
     * @param {string} key - Clave de la tesela a eliminar.
     */
    _removeTile(key) {
        const tile = this._tiles[key];
        if (tile) {
            this.removeFromParent(tile.el);
            delete this._tiles[key];
            delete this.dataTiles[key];
        }
    }
});

export default TiledPoiLayer;
